import { Download } from "@repo/icons";
import { CpsToast } from "corpus";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { FileOpener } from "@capawesome-team/capacitor-file-opener";
import { useState } from "react";
import { iosOrAndroid } from "@repo/lib";
import { Button, type ButtonProps } from "@/components/button";

type DownloadFileButtonProps = ButtonProps & {
  title: string;
  fileContent: string;
  fileName: string;
  fileExtension: "csv";
  onClick?: () => void;
};

export const DownloadFileButton = ({
  title,
  fileName,
  fileContent,
  fileExtension,
  onClick,
  ...props
}: DownloadFileButtonProps): JSX.Element => {
  const [isLoading, setIsLoading] = useState(false);
  const [openSuccessToast, setOpenSuccessToast] = useState(false);
  const [openErrorToast, setOpenErrorToast] = useState(false);

  const webFileDownload = (): void => {
    const blob = new Blob([fileContent], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${fileName}.${fileExtension}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const writeAndOpenFile = async (): Promise<void> => {
    onClick?.();
    setIsLoading(true);
    try {
      if (iosOrAndroid) {
        const { uri } = await Filesystem.writeFile({
          path: `${fileName}.${fileExtension}`,
          data: fileContent,
          directory: Directory.Documents,
          encoding: Encoding.UTF8,
        });
        await FileOpener.openFile({ path: uri });
      } else {
        webFileDownload();
      }
      setOpenSuccessToast(true);
    } catch (err) {
      setOpenErrorToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        Icon={Download}
        onClick={() => void writeAndOpenFile()}
        rounded
        fullWidth
        loading={isLoading}
        size="small"
        {...props}
      >
        {title}
      </Button>

      <CpsToast
        title="Arquivo baixado com sucesso. Você pode encontrá-lo no diretório de documentos do seu dispositivo."
        show={openSuccessToast}
        onClose={() => setOpenSuccessToast(false)}
        type="success"
        duration={10}
      />

      <CpsToast
        title="Ocorreu um erro ao baixar o arquivo. Espere alguns minutos e tente novamente. Caso o erro persista, entre em contato com nosso suporte."
        show={openErrorToast}
        onClose={() => setOpenErrorToast(false)}
        type="danger"
        duration={10}
      />
    </>
  );
};
